import React, { useEffect, useMemo, useRef, useState } from "react";

import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { valueToString } from "helpers/Helpers";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import classNames from "classnames";

import { Column, ColumnInstance, Row, useExpanded, useGroupBy, useTable } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import TableFilterDropdown from "components/tableFilterDropdown/TableFilterDropdown";

import "components/reportTable/reportTable.scss";
import { useReportPresentation } from "../ReportPresentationContext";
import { formatReportCurrency } from "helpers/reportFormat";

interface IProps {
  data: Array<Record<string, any>>;
  columns: Array<Record<string, any>>;
  description: string;
  compileDate: string;
  cellOnClick: (cell: any) => void;
  cellData: (columnId: string, cellValue: string, displayType: string, cell?: any) => void;
  rowClassNames?: (row: any) => void;
  cellClassNames: (cell: any, isRowLocked: boolean) => void;
  headerClassNames?: (column: any) => string;
  footerData: (columnId: string, footerRow?: Record<string, any>) => React.ReactNode;
  hideFooter?: boolean;
  expanding: boolean;
  expandingColumn?: string;
  truncateHeaders?: boolean;
  truncateLength?: number;
  setFilteredColumns?: (columns: Array<ColumnInstance<Record<string, any>>>) => void;
  toolbarActions?: React.ReactNode;
}

interface ILockedColumn {
  id: string;
  offset: number;
}

export default function BaseReportTable(props: IProps) {
  const [columnsState, setColumnsState] = useState<ColumnInstance<Record<string, any>>[]>([]);
  const [lockedColumns, setLockedColumns] = useState<ILockedColumn[]>([]);
  const tableColumnsRef = useRef<HTMLTableRowElement>(null);
  const [tableColumnsWidth, setTableColumnsWidth] = useState<number>(undefined);
  const windowSize = useWindowSize();
  const { setHiddenColumns, setGroupBy } = useReportPresentation();

  const { cellOnClick, cellData, rowClassNames, cellClassNames, headerClassNames, footerData } = props;

  function formatDisplay(type: string, value: string) {
    let newValue = value !== null && value !== undefined ? value : "";

    if (!isNaN(Number(newValue)) && newValue !== "") {
      switch (type) {
        case "currency":
          newValue = formatReportCurrency(value);
          break;
        case "decimal":
          newValue = Number(value).toFixed(2);
          break;
        case "percentage":
          newValue = `${(Number(value) * 100).toFixed(2)}%`;
          break;
        case "seconds":
          newValue = formatSeconds(Number(value)); 
          break;
        default:
          break;
      }
    } else if (type === "string" && newValue !== "") {
      newValue = valueToString(value).replace(/(^\w|\s\w)/g, c => c.toUpperCase());
    }

    return newValue;
  }

  //Set columns and subcolumns
  function getColumns(colArray: Array<Record<string, any>>) {
    const columnsArray: Column<Record<string, any>>[] =
      colArray?.length > 0
        ? colArray?.map(col => {
          const columnId = col.id.toString();
          return {
            Header:
              columnId === props.expandingColumn
                ? ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }: any) => (
                  <span {...getToggleAllRowsExpandedProps()}>
                    {col.display}
                    {isAllRowsExpanded ? (
                      <FontAwesomeIcon fixedWidth icon={["far", "chevron-down"]} />
                    ) : (
                      <FontAwesomeIcon fixedWidth icon={faChevronRight} />
                    )}
                  </span>
                )
                : col.display ?? "",
            id: columnId,
            locked: col.locked,
            accessor: col.subColumns ? undefined : columnId,
            columns: !col.subColumns ? undefined : getColumns(col.subColumns as Array<Record<string, any>>),
            show_with_parent: col.show_with_parent,
            display_type: col?.type,
            aggregate: col?.aggregate ? col?.aggregate : undefined,
            Aggregated: ({ value }: any) =>
              `${formatDisplay(col.type as string, value as string)} ${col?.aggregateText ? col?.aggregateText : ""}`,
            disableGroupBy: !col?.canGroup,
          };
        })
        : [];
    return columnsArray;
  }

  const reportsTableData = useMemo(() => props?.data?.filter(row => !row.is_footer), [props.data]);

  const reportsDataColumns: any = useMemo(() => [...getColumns(props.columns)], [props.columns]);

  // Footer rows
  const footerRows = useMemo(
    () => props?.data?.filter(row => row?.is_footer),
    [props.data],
  );

  // initialState: {hiddenColumns: ["number"]} <- column ids fill this array
  const tableInstance = useTable({ columns: reportsDataColumns, data: reportsTableData }, useGroupBy, useExpanded);
  const { getTableProps, getTableBodyProps, headerGroups, footerGroups, rows, prepareRow, allColumns, state } = tableInstance;
  const { hiddenColumns } = state;

  const tableState = state as typeof state & {
    groupBy?: string[];
  };

  
  useEffect(() => {
    if (setHiddenColumns) {
      setHiddenColumns(hiddenColumns ?? []);
    }
  }, [hiddenColumns, setHiddenColumns]);

  useEffect(() => {
    if (setGroupBy) {
      setGroupBy(tableState.groupBy ?? []);
    }
  }, [tableState?.groupBy, setGroupBy]);

  useEffect(() => {
    const filteredColumns = allColumns?.filter(column => column.id !== "expander");
    setColumnsState(filteredColumns);
  }, [allColumns]);

  useEffect(() => {
    if (props.setFilteredColumns) {
      props.setFilteredColumns(headerGroups[0].headers);
    }
  }, [headerGroups]);

  useEffect(() => {
    if (!tableColumnsRef?.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (tableColumnsRef?.current) {
        // Prefer a body row for widths when multi-row headers put root leaves (Date/Total)
        // in the top group via placeholders, so the leaf header row ref may omit them.
        const table = tableColumnsRef.current.closest("table");
        const measureRow = table?.querySelector("tbody tr") ?? tableColumnsRef.current;
        let tableColumnsWidth = 0;

        for (let i = 0; i < measureRow.cells.length; i++) {
          tableColumnsWidth += measureRow.cells[i].getBoundingClientRect().width;
        }

        setTableColumnsWidth(tableColumnsWidth);
      }
    });

    resizeObserver.observe(tableColumnsRef.current);
    return () => resizeObserver.disconnect();
  }, [tableColumnsRef?.current]);

  useEffect(() => {
    if (tableColumnsRef.current) {
      const lockedHeaders: Array<{ id: string; locked: boolean; index: number }> = headerGroups?.[
        headerGroups.length - 1
      ]?.headers
        ?.map((header: any, index: number) => ({ id: header.id, locked: header.locked || header.isGrouped, index }))
        ?.filter(header => header.locked);

      const table = tableColumnsRef.current.closest("table");
      const measureRow = table?.querySelector("tbody tr") ?? tableColumnsRef.current;

      const lockedColumns: ILockedColumn[] = [];
      let offset = 0;

      lockedHeaders.forEach(lockedHeader => {
        lockedColumns.push({ id: lockedHeader.id, offset });
        offset += measureRow.cells[lockedHeader.index]?.getBoundingClientRect().width ?? 0;
      });

      setLockedColumns(lockedColumns);
    }
  }, [headerGroups, tableColumnsWidth]);

  //Hide subcolumns based on the header's show_with_parent value
  function handleColumnClick(column: any) {
    column?.headers?.forEach((header: any) => {
      if (header?.show_with_parent === false) {
        header.toggleHidden();
      }
    });
  }

  function checkLockedColumn(lockedColumn: any) {
    return (
      lockedColumn !== undefined &&
      lockedColumn?.offset !== undefined &&
      (windowSize.width > MOBILE_WIDTH ||
        lockedColumn.id === lockedColumns?.[0]?.id ||
        lockedColumn.id === lockedColumns?.[1]?.id)
    );
  }

  function resolveHeaderAlignClasses(column: any) {
    return headerClassNames?.(column) ?? "";
  }

  function textAlignFromHeaderClasses(alignClasses: string): "left" | "right" | undefined {
    if (!headerClassNames) {
      return undefined;
    }
    if (alignClasses.includes("is-numeric") || alignClasses.includes("report-table-cell-currency")) {
      return "right";
    }
    return "left";
  }

  return props.data && props.data?.length > 0 ? (
    <>
      <div className="report-table-filter-button-container">
        <TableFilterDropdown columns={columnsState} tableInstance={tableInstance} />
        {props.toolbarActions}
      </div>

      <div className="report-table-container">
        <table {...getTableProps()} className="report-table-dynamic">
          <thead className="report-table-header">
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <tr
                key={headerGroupIndex}
                {...headerGroup.getHeaderGroupProps()}
                ref={headerGroupIndex === headerGroups.length - 1 ? tableColumnsRef : null}
                className="report-table-header"
              >
                {headerGroup.headers.map((column: any, columnIndex) => {
                  // react-table inserts empty placeholder cells above root leaf columns (Date, Total).
                  // Do NOT use rowspan: position:sticky is unreliable on rowspan cells in Chromium,
                  // which lets locked body cells paint into the header band. Render the label in the
                  // top row and an opaque sticky filler in lower header rows instead.
                  if (column.placeholderOf) {
                    const leafColumn = column.placeholderOf;
                    const lockedColumn = lockedColumns?.find(
                      lockedColumn => lockedColumn?.id === leafColumn?.id,
                    );
                    const isColumnLocked = checkLockedColumn(lockedColumn);
                    const headerLabel =
                      typeof leafColumn.Header === "string" ? leafColumn.Header : leafColumn.render("Header");
                    const alignClasses = resolveHeaderAlignClasses(leafColumn);
                    const textAlign = textAlignFromHeaderClasses(alignClasses);
                    const headerProps = column.getHeaderProps();

                    return (
                      <td
                        key={columnIndex}
                        {...headerProps}
                        style={{
                          ...(headerProps as { style?: React.CSSProperties }).style,
                          ...(isColumnLocked ? { left: lockedColumn.offset } : {}),
                          ...(textAlign ? { textAlign } : {}),
                        }}
                        className={classNames(
                          (headerProps as { className?: string }).className,
                          {
                            "report-table-column-locked": isColumnLocked,
                          },
                          alignClasses,
                        )}
                      >
                        <div className={alignClasses || (leafColumn.display_type === "currency" ? "report-table-cell-currency" : "")}>
                          {headerLabel}
                        </div>
                      </td>
                    );
                  }

                  // Root leaf label already rendered via placeholder in the top header group —
                  // emit an empty sticky filler so the header band stays covered when scrolling.
                  if (headerGroupIndex > 0 && !column.parent) {
                    const lockedColumn = lockedColumns?.find(lockedColumn => lockedColumn?.id === column?.id);
                    const isColumnLocked = checkLockedColumn(lockedColumn);
                    const alignClasses = resolveHeaderAlignClasses(column);
                    const textAlign = textAlignFromHeaderClasses(alignClasses);
                    const headerProps = column.getHeaderProps();

                    return (
                      <td
                        key={columnIndex}
                        {...headerProps}
                        aria-hidden="true"
                        style={{
                          ...(headerProps as { style?: React.CSSProperties }).style,
                          ...(isColumnLocked ? { left: lockedColumn.offset } : {}),
                          ...(textAlign ? { textAlign } : {}),
                        }}
                        className={classNames(
                          (headerProps as { className?: string }).className,
                          "report-table-header-rowspan-filler",
                          {
                            "report-table-column-locked": isColumnLocked,
                          },
                          alignClasses,
                        )}
                      />
                    );
                  }

                  const lockedColumn = lockedColumns?.find(lockedColumn => lockedColumn?.id === column?.id);
                  const isColumnLocked = checkLockedColumn(lockedColumn);

                  if (column?.columns) {
                    return (
                      <td key={columnIndex} {...column.getHeaderProps()} onClick={() => handleColumnClick(column)}>
                        {column.render("Header")}
                      </td>
                    );
                  } else {
                    const alignClasses = resolveHeaderAlignClasses(column);
                    const textAlign = textAlignFromHeaderClasses(alignClasses);
                    const headerProps = column.getHeaderProps();

                    return (
                      <td
                        key={columnIndex}
                        {...headerProps}
                        style={{
                          ...(headerProps as { style?: React.CSSProperties }).style,
                          ...(isColumnLocked ? { left: lockedColumn.offset } : {}),
                          ...(textAlign ? { textAlign } : {}),
                        }}
                        className={classNames(
                          (headerProps as { className?: string }).className,
                          {
                            "report-table-column-locked": isColumnLocked,
                          },
                          alignClasses,
                        )}
                      >
                        <div className={alignClasses || (column.display_type === "currency" ? "report-table-cell-currency" : "")}>
                          {column.canGroupBy ? (
                            // If the column can be grouped, let's add a toggle
                            <span {...column.getGroupByToggleProps()}>
                              {column.isGrouped ? (
                                <FontAwesomeIcon className="mr-1" fixedWidth icon={["far", "octagon-minus"]} />
                              ) : (
                                <FontAwesomeIcon className="mr-1" fixedWidth icon={["far", "octagon-plus"]} />
                              )}
                            </span>
                          ) : null}
                          {props.truncateHeaders
                            ? String(column.render("Header"))?.length > props.truncateLength
                              ? `${String(column.render("Header"))?.slice(0, props.truncateLength - 1)}...`
                              : column.render("Header")
                            : column.render("Header")}
                        </div>
                      </td>
                    );
                  }
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row: any, indx: number) => {
              prepareRow(row as Row<object>);
              return (
                <tr
                  key={indx}
                  {...row.getRowProps()}
                  className={rowClassNames ? rowClassNames(row) : "report-table-row"}
                >
                  {row.cells.map((cell: any, index: number) => {
                    const lockedColumn = lockedColumns?.find(lockedColumn => lockedColumn?.id === cell?.column?.id);
                    const isRowLocked = checkLockedColumn(lockedColumn);
                    const alignClassName = String(cellClassNames(cell, isRowLocked) ?? "");
                    // When a report opts into headerClassNames (e.g. RevPAR), mirror that
                    // alignment onto body cells with inline styles so shared SCSS cannot win.
                    const textAlign = headerClassNames
                      ? textAlignFromHeaderClasses(alignClassName) ?? "left"
                      : undefined;

                    return (
                      <td
                        key={index}
                        {...cell.getCellProps()}
                        {...(row.canExpand && props.expanding && cell.column.id === props.expandingColumn
                          ? row.getToggleRowExpandedProps()
                          : null)}
                        style={{
                          left: isRowLocked ? lockedColumn.offset : "auto",
                          cursor: row.canExpand && cell.column.id === props.expandingColumn ? "pointer" : "auto",
                          ...(textAlign ? { textAlign } : {}),
                        }}
                        className={alignClassName}
                        {...(!(props.expanding && row.canExpand && cell.column.id === props.expandingColumn)
                          ? { onClick: () => cellOnClick(cell) }
                          : {})}
                      >
                        <div
                          className={classNames(alignClassName, {
                            "report-table-cell-currency": cell.column.display_type === "currency",
                          })}
                          style={textAlign ? { textAlign, width: "100%" } : undefined}
                        >
                          {cell.column.id === props.expandingColumn && (
                            <span
                              {...row.getToggleRowExpandedProps({
                                style: {
                                  paddingLeft: `${row.depth * 1.1}rem`,
                                },
                              })}
                            />
                          )}
                          {cell.isGrouped && (
                            <span {...row.getToggleRowExpandedProps()}>
                              {row.isExpanded ? (
                                <FontAwesomeIcon fixedWidth icon={["far", "chevron-down"]} />
                              ) : (
                                <FontAwesomeIcon fixedWidth icon={faChevronRight} />
                              )}
                            </span>
                          )}
                          {(!props.expanding || !row.canExpand || cell.column.id !== props.expandingColumn) &&
                            (cell.isAggregated
                              ? cell.render("Aggregated")
                              : cellData(cell.column.id as string, cell.value as string, cell?.column?.display_type as string, cell))}
                          {cell.isGrouped && `(${String(row.subRows.length)})`}
                          {props.expanding && row.canExpand && cell.column.id === props.expandingColumn ? (
                            <span>
                              {cell.isAggregated
                                ? cell.render("Aggregated")
                                : cellData(cell.column.id as string, cell.value as string, cell?.column?.display_type as string, cell)}
                              {row.isExpanded ? (
                                <FontAwesomeIcon fixedWidth icon={["far", "chevron-down"]} />
                              ) : (
                                <FontAwesomeIcon fixedWidth icon={faChevronRight} />
                              )}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {!props.hideFooter && (
            <tfoot className="table-footer">
              {footerRows && footerRows?.length > 0 ? (
              footerRows.map((footerRow, footerRowIndex) => (
                <tr
                  key={footerRowIndex}
                  className="report-table-footer"
                >
                  {(footerGroups[footerGroups.length - 1] ?? footerGroups[0])?.headers.map((column: any, columnIndex) => {
                    const lockedColumn = lockedColumns?.find(
                      lockedColumn => lockedColumn?.id === column?.id,
                    );
            
                    const isColumnLocked = checkLockedColumn(lockedColumn);
                    const alignClasses = resolveHeaderAlignClasses(column);
                    const textAlign = textAlignFromHeaderClasses(alignClasses);
            
                    return (
                      <td
                        key={columnIndex}
                        style={{
                          ...(isColumnLocked ? { left: lockedColumn.offset } : {}),
                          ...(textAlign ? { textAlign } : {}),
                        }}
                        className={classNames(
                          {
                            "report-table-footer-locked": isColumnLocked,
                            "report-table-cell-currency": column.display_type === "currency",
                          },
                          alignClasses,
                        )}
                      >
                        {footerData(column.id as string, footerRow)}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : footerGroups.map((footerGroup, footerGroupIndex) => (
              <tr
                key={footerGroupIndex}
                {...footerGroup.getHeaderGroupProps()}
                ref={footerGroupIndex === headerGroups.length - 1 ? tableColumnsRef : null}
                className="report-table-footer"
              >
                {footerGroup.headers.map((column: any, columnIndex) => {
                  const lockedColumn = lockedColumns?.find(lockedColumn => lockedColumn?.id === column?.id);
                  const isColumnLocked = checkLockedColumn(lockedColumn);
                  const alignClasses = resolveHeaderAlignClasses(column);
                  const textAlign = textAlignFromHeaderClasses(alignClasses);
                  const footerProps = column.getFooterProps();

                  return (
                    <td
                      key={columnIndex}
                      {...footerProps}
                      style={{
                        ...(footerProps as { style?: React.CSSProperties }).style,
                        ...(isColumnLocked ? { left: lockedColumn.offset } : {}),
                        ...(textAlign ? { textAlign } : {}),
                      }}
                      className={classNames(
                        (footerProps as { className?: string }).className,
                        {
                          "report-table-footer-locked": isColumnLocked,
                          "report-table-cell-currency": column.display_type === "currency",
                        },
                        alignClasses,
                      )}
                    >
                      {footerData(column.id as string)}
                    </td>
                  );
                })}
              </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
    </>
  ) : (
    <div></div>
  );
}

export function formatSeconds(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}:${minutes.toString().padStart(2, "0")}`;
}
